import React, { useState, useEffect } from "react";
import Swal from "sweetalert2";
import { useNavigate } from 'react-router-dom';
import { LogOut } from 'lucide-react';

// Enhanced, clearer UI components
const EducationFormSection = ({
  title,
  level,
  fields,
  data,
  onInputChange,
  onFileChange,
  certificateFile,
  required = false,
  extraRequirement = null,
}) => (
  <div className="mb-6 border border-gray-200 p-4 rounded-md shadow-sm bg-gray-50">
    <div className="flex items-center mb-2">
      <h3
        className="block text-sky-700 uppercase font-semibold tracking-wide"
        style={{ fontSize: ".9em" }}
      >
        {title}
      </h3>
      {required && (
        <span
          className="ml-2 text-red-600 font-bold text-lg"
          title="This section is required"
        >
          *
        </span>
      )}
      {extraRequirement && (
        <span className="ml-3 px-2 py-0.5 bg-yellow-100 text-yellow-800 text-xs rounded">
          {extraRequirement}
        </span>
      )}
    </div>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {fields.map(({ name, label }) => (
        <div className="mb-3" key={name}>
          <label className="block text-sm font-medium text-gray-700 fw-bold">
            {label}
          </label>
          <input
            type="text"
            value={data[name] || ""}
            onChange={(e) => onInputChange(level, name, e.target.value)}
            placeholder={`Enter ${label.toLowerCase()}`}
            className="mt-1 w-full rounded-md border border-gray-300 bg-white text-sm text-gray-700 shadow-sm focus:ring-2 focus:ring-sky-200 focus:border-sky-400 transition duration-100"
            autoComplete="off"
          />
        </div>
      ))}
      <div className="mb-3">
        <label className="block text-sm font-medium text-gray-700 fw-bold">
          Certificate {required && <span className="text-red-600">*</span>}
        </label>
        <input
          type="file"
          accept=".pdf"
          onChange={(e) => onFileChange(e.target.files[0])}
          className="block w-full text-sm file:font-semibold text-gray-700 file:mr-4 file:py-2 file:px-4 border border-gray-200 mt-2 cursor-pointer"
        />
        {certificateFile ? (
          typeof certificateFile === "string" ? (
            <div className="mt-1">
              <a
                href={certificateFile}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:underline text-sm"
              >
                View Uploaded Certificate
              </a>
            </div>
          ) : (
            <div className="text-green-600 text-xs mt-1">
              File ready to upload: {certificateFile.name}
            </div>
          )
        ) : null}
      </div>
    </div>
  </div>
);

const EducationAndCertificatesStep = ({ onNext, onBack }) => {
  const [educationData, setEducationData] = useState({
    id: null,
    secondary: { school: "", grade: "", year: "" },
    diploma: { institution: "", course: "", grade: "", year: "" },
    higherDiploma: { institution: "", course: "", grade: "", year: "" },
    university: { institution: "", course: "", grade: "", year: "" },
    masters: { institution: "", course: "", grade: "", year: "" },
    phd: { institution: "", course: "", grade: "", year: "" },
    other: { institution: "", course: "", grade: "", year: "" },
  });

  const [certificates, setCertificates] = useState({
    id: null,
    phd: null,
    master: null,
    bachelor: null,
    higher_diploma: null,
    diploma: null,
    kcse: null,
    other: null,
  });

  const [isNextEnabled, setIsNextEnabled] = useState(false);

  // Track which requirements are missing for info TODO: use for user feedback
  const [missingSections, setMissingSections] = useState({
    diplomaOrBachelor: false,
  });

  // Track internal completion for Step 3
  const [step3Completed, setStep3Completed] = useState(false);

  // Fetch user information (education details) and certificates (uploads) separately
  useEffect(() => {
    const fetchEducationDetails = async () => {
      const apiUrl = "https://portal.lsb.go.ke/account/education_details/";
      const accessToken = localStorage.getItem("accessToken");
      try {
        const response = await fetch(apiUrl, {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.detail || "Failed to fetch education details.");
        }
        const data = await response.json();
        if (data.length > 0) {
          const userData = data[0];
          setEducationData((prev) => ({
            ...prev,
            id: userData.user_id || null,
            secondary: {
              school: userData.secondary_school || "",
              grade: userData.secondary_school_grade || "",
              year: userData.secondary_school_years || "",
            },
            diploma: {
              institution: userData.diploma_institution || "",
              course: userData.diploma_course || "",
              grade: userData.diploma_grade || "",
              year: userData.diploma_years || "",
            },
            higherDiploma: {
              institution: userData.higher_dip_institution || "",
              course: userData.higher_dip_course || "",
              grade: userData.higher_dip_grade || "",
              year: userData.higher_dip_years || "",
            },
            university: {
              institution: userData.university_institution || "",
              course: userData.university_course || "",
              grade: userData.university_grade || "",
              year: userData.university_years || "",
            },
            masters: {
              institution: userData.masters_institution || "",
              course: userData.masters_course || "",
              grade: userData.masters_grade || "",
              year: userData.masters_years || "",
            },
            phd: {
              institution: userData.phd_institution || "",
              course: userData.phd_course || "",
              grade: userData.phd_grade || "",
              year: userData.phd_years || "",
            },
            other: {
              institution:
                userData.other_description ||
                userData.other_institution ||
                "",
              course: userData.other_course || "",
              grade: userData.other_grade || "",
              year: userData.other_years || "",
            },
          }));

          // Patch: set the certificates.id as well WHEN education details fetch succeeds and you have user_id!
          setCertificates((prev) => ({
            ...prev,
            id: userData.user_id || prev.id, // Update id if available
          }));
        }
      } catch (error) {
        console.error("Error fetching education details:", error.message);
        Swal.fire("Error", error.message, "error");
      }
    };

    const fetchCertificates = async () => {
      const apiUrl = "https://portal.lsb.go.ke/account/education_certificates/";
      const accessToken = localStorage.getItem("accessToken");
      try {
        const response = await fetch(apiUrl, {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.detail || "Failed to fetch certificate uploads.");
        }
        const data = await response.json();
        if (data.length > 0) {
          const userData = data[0];
          setCertificates((prev) => ({
            ...prev,
            id: userData.user_id,
            phd: userData.phd,
            master: userData.master,
            bachelor: userData.bachelor,
            higher_diploma: userData.higher_diploma,
            diploma: userData.diploma,
            kcse: userData.kcse,
            other: userData.other || null,
          }));
          // If user's data already marks step_3 completed (from backend), reflect that in state if needed (not end-user visible)
          if (userData.step_3_completed) setStep3Completed(true);
        }
      } catch (error) {
        console.error("Error fetching certificate uploads:", error.message);
        Swal.fire("Error", error.message, "error");
      }
    };

    fetchEducationDetails();
    fetchCertificates();
  }, []);

  // Enforce requirements: [diploma OR university bachelor's] only (secondary optional)
  useEffect(() => {
    // Diploma
    const dip = educationData.diploma;
    const hasDiploma =
      !!dip.institution &&
      !!dip.course &&
      !!dip.grade &&
      !!dip.year &&
      !!certificates.diploma;

    // University (Bachelor's)
    const uni = educationData.university;
    const hasBachelor =
      !!uni.institution &&
      !!uni.course &&
      !!uni.grade &&
      !!uni.year &&
      !!certificates.bachelor;

    // At least diploma or bachelor's
    const hasDiplomaOrBachelor = hasDiploma || hasBachelor;

    setIsNextEnabled(hasDiplomaOrBachelor);

    // For user feedback
    setMissingSections({
      diplomaOrBachelor: !hasDiplomaOrBachelor,
    });
  }, [
    educationData.diploma,
    certificates.diploma,
    educationData.university,
    certificates.bachelor,
  ]);

  const handleInputChange = (level, field, value) => {
    setEducationData((prev) => ({
      ...prev,
      [level]: {
        ...prev[level],
        [field]: value,
      },
    }));
  };

  const handleFileChange = (level, file) => {
    setCertificates((prev) => ({
      ...prev,
      [level]: file,
    }));
  };

  // Main Next handler including the two booleans: education_data_completed and step_3
  const handleNext = async () => {
    // Re-validate before submit
    const dip = educationData.diploma;
    const hasDiploma =
      !!dip.institution &&
      !!dip.course &&
      !!dip.grade &&
      !!dip.year &&
      !!certificates.diploma;
    const uni = educationData.university;
    const hasBachelor =
      !!uni.institution &&
      !!uni.course &&
      !!uni.grade &&
      !!uni.year &&
      !!certificates.bachelor;
    const hasDiplomaOrBachelor = hasDiploma || hasBachelor;

    // The two booleans for server
    const education_data_completed = !!hasDiplomaOrBachelor;
    const step_3 = !!hasDiplomaOrBachelor;

    if (!hasDiplomaOrBachelor) {
      Swal.fire(
        "Missing Required Info",
        "You must enter either Diploma OR University (Bachelor's) education and upload the respective certificate.",
        "warning"
      );
      return;
    }

    const detailsApiUrl = "https://portal.lsb.go.ke/account/education_details/";
    const certificatesApiUrl =
      "https://portal.lsb.go.ke/account/education_certificates/";
    const personalApiUrl = "https://portal.lsb.go.ke/account/personal_data/";
    const accessToken = localStorage.getItem("accessToken");

    // --- 1. Submit education details (inputs) ---
    let user_id_to_use = null;

    try {
      const eduDetailsPayload = {
        id: educationData.id,
        secondary_school: educationData.secondary.school || "",
        secondary_school_grade: educationData.secondary.grade || "",
        secondary_school_years: educationData.secondary.year || "",
        diploma_institution: educationData.diploma.institution || "",
        diploma_course: educationData.diploma.course || "",
        diploma_grade: educationData.diploma.grade || "",
        diploma_years: educationData.diploma.year || "",
        higher_dip_institution: educationData.higherDiploma.institution || "",
        higher_dip_course: educationData.higherDiploma.course || "",
        higher_dip_grade: educationData.higherDiploma.grade || "",
        higher_dip_years: educationData.higherDiploma.year || "",
        university_institution: educationData.university.institution || "",
        university_course: educationData.university.course || "",
        university_grade: educationData.university.grade || "",
        university_years: educationData.university.year || "",
        masters_institution: educationData.masters.institution || "",
        masters_course: educationData.masters.course || "",
        masters_grade: educationData.masters.grade || "",
        masters_years: educationData.masters.year || "",
        phd_institution: educationData.phd.institution || "",
        phd_course: educationData.phd.course || "",
        phd_grade: educationData.phd.grade || "",
        phd_years: educationData.phd.year || "",
        // For Other Certificate
        other_institution:
          (educationData.other && educationData.other.institution) || "",
        other_course: (educationData.other && educationData.other.course) || "",
        other_grade: (educationData.other && educationData.other.grade) || "",
        other_years: (educationData.other && educationData.other.years) || "",
        // --- Add the two required booleans here
        education_data_completed: education_data_completed,
        step_3: step_3,
      };

      const eduDetailsResponse = await fetch(detailsApiUrl, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify(eduDetailsPayload),
      });
      if (!eduDetailsResponse.ok) {
        const errorData = await eduDetailsResponse.json();
        throw new Error(
          errorData.detail || "Failed to update education details."
        );
      }
      const updatedDetails = await eduDetailsResponse.json();
      user_id_to_use =
        updatedDetails.user_id ||
        updatedDetails.id ||
        educationData.id ||
        localStorage.getItem("user_id");

      // Patch state for followup use for certificates
      setCertificates((prev) => ({
        ...prev,
        id: user_id_to_use,
      }));
    } catch (error) {
      Swal.fire("Error", error.message, "error");
      return;
    }

    // --- 2. Submit certificates (PDF files) to education_certificates API ---
    // Make sure we always use user_id from education details
    const formData = new FormData();

    const userIdForCertificates =
      user_id_to_use ||
      educationData.id ||
      certificates.id ||
      localStorage.getItem("user_id");

    formData.append("id", userIdForCertificates);

    // Only file uploads
    if (certificates.kcse && certificates.kcse instanceof File) {
      formData.append("kcse", certificates.kcse);
    }
    if (certificates.diploma && certificates.diploma instanceof File) {
      formData.append("diploma", certificates.diploma);
    }
    if (
      certificates.higher_diploma &&
      certificates.higher_diploma instanceof File
    ) {
      formData.append("higher_diploma", certificates.higher_diploma);
    }
    if (certificates.bachelor && certificates.bachelor instanceof File) {
      formData.append("bachelor", certificates.bachelor);
    }
    if (certificates.master && certificates.master instanceof File) {
      formData.append("master", certificates.master);
    }
    if (certificates.phd && certificates.phd instanceof File) {
      formData.append("phd", certificates.phd);
    }
    if (certificates.other && certificates.other instanceof File) {
      formData.append("other", certificates.other);
    }
    // Add education_data_completed and step_3 as booleans
    formData.append(
      "education_data_completed",
      education_data_completed ? "true" : "false"
    );
    formData.append("step_3", step_3 ? "true" : "false");

    try {
      const certificatesResponse = await fetch(certificatesApiUrl, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
        body: formData,
      });

      if (!certificatesResponse.ok) {
        const errorData = await certificatesResponse.json();
        throw new Error(
          errorData.detail || "Failed to update education certificates."
        );
      }

      // --- 3. Mark Step 3 as completed in personal_data ---
      const stepUserId =
        user_id_to_use ||
        educationData.id ||
        certificates.id ||
        localStorage.getItem("user_id");

      if (stepUserId) {
        const formStep3 = new FormData();
        formStep3.append("education_completed", true);
        formStep3.append("id", stepUserId);
        formStep3.append("step_3", true);

        const stepResponse = await fetch(personalApiUrl, {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
          body: formStep3,
        });

        if (!stepResponse.ok) {
          throw new Error("Failed to mark step 3 as completed.");
        }
      }

      Swal.fire(
        "Success",
        "Education details and certificates updated successfully!",
        "success"
      );
      // Mark as completed in state (internal)
      setStep3Completed(true);
      onNext();
    } catch (error) {
      Swal.fire("Error", error.message, "error");
    }
  };

  // User feedback
  const requirementMessage = (
    <div className="pl-3 text-gray-700 mb-4 mt-2 leading-snug">
      <p className="text-red-700 font-bold underline underline-offset-4 decoration-red-700">
        [ All certificates should be in PDF format. ]
      </p>
      <p>
        * Provide your current{" "}
        <b className="font-semibold text-red-700">Education</b> details and{" "}
        <b className="font-semibold text-red-700">corresponding certificates.</b>
      </p>
      <p>
        * You must provide either{" "}
        <b className="font-semibold text-red-700">Diploma</b> or{" "}
        <b className="text-red-700 font-semibold">
          University (Bachelor’s)
        </b>{" "}
        details.
      </p>
      {missingSections.diplomaOrBachelor && (
        <p className="text-yellow-700 mt-2">
          * Please provide either <b className="font-semibold">Diploma</b>{" "}
          <b className="font-normal">OR</b>{" "}
          <b className="font-semibold">University (Bachelor’s)</b> details and
          upload the relevant certificate.
        </p>
      )}
    </div>
  );

  const navigate = useNavigate();

  const handleLogout = () => {
    localStorage.removeItem('accessToken');
    Swal.fire({
      title: 'Success',
      text: 'You have been logged out.',
      icon: 'success',
      width: 370,
    });
    navigate('/portal');
  };

  return (
    <div>
      <div className="flex justify-between items-center pb-6">
        <h3 className="font-bold text-[#3d0707] tracking-wide underline underline-offset-4 decoration-[#ebcab3]">
          Education and Certificates
        </h3>

        <button
          onClick={handleLogout}
          className="py-2 px-3 bg-red-600 hover:font-bold uppercase text-white flex gap-2 items-center rounded-md text-sm transition-transform duration-200 ease-in-out hover:scale-105 active:scale-95 focus:outline-none focus:ring-2 focus:ring-red-400 shadow-sm hover:shadow-lg"
        >
          <span className="transition duration-200 ease-in-out group-hover:pr-1">Logout</span>
          <LogOut className="w-5 h-5 transition-transform duration-200 group-hover:rotate-[-15deg]" />
        </button>
      </div>

      <div className="mb-6 leading-tight">
        {requirementMessage}
      </div>

      <form>
        <EducationFormSection
          title="Secondary Education"
          level="secondary"
          fields={[
            { name: "school", label: "School" },
            { name: "grade", label: "Grade" },
            { name: "year", label: "Year" },
          ]}
          data={educationData.secondary}
          onInputChange={handleInputChange}
          onFileChange={(file) => handleFileChange("kcse", file)}
          certificateFile={certificates.kcse}
        />
        <EducationFormSection
          title="Diploma"
          level="diploma"
          fields={[
            { name: "institution", label: "Institution" },
            { name: "course", label: "Course" },
            { name: "grade", label: "Grade" },
            { name: "year", label: "Year" },
          ]}
          data={educationData.diploma}
          onInputChange={handleInputChange}
          onFileChange={(file) => handleFileChange("diploma", file)}
          certificateFile={certificates.diploma}
        />
        <EducationFormSection
          title="Higher Diploma"
          level="higherDiploma"
          fields={[
            { name: "institution", label: "Institution" },
            { name: "course", label: "Course" },
            { name: "grade", label: "Grade" },
            { name: "year", label: "Year" },
          ]}
          data={educationData.higherDiploma}
          onInputChange={handleInputChange}
          onFileChange={(file) => handleFileChange("higher_diploma", file)}
          certificateFile={certificates.higher_diploma}
        />
        <EducationFormSection
          title="University (Bachelor's)"
          level="university"
          fields={[
            { name: "institution", label: "Institution" },
            { name: "course", label: "Course" },
            { name: "grade", label: "Grade" },
            { name: "year", label: "Year" },
          ]}
          data={educationData.university}
          onInputChange={handleInputChange}
          onFileChange={(file) => handleFileChange("bachelor", file)}
          certificateFile={certificates.bachelor}
        />
        <EducationFormSection
          title="Masters"
          level="masters"
          fields={[
            { name: "institution", label: "Institution" },
            { name: "course", label: "Course" },
            { name: "grade", label: "Grade" },
            { name: "year", label: "Year" },
          ]}
          data={educationData.masters}
          onInputChange={handleInputChange}
          onFileChange={(file) => handleFileChange("master", file)}
          certificateFile={certificates.master}
        />
        <EducationFormSection
          title="PhD"
          level="phd"
          fields={[
            { name: "institution", label: "Institution" },
            { name: "course", label: "Course" },
            { name: "grade", label: "Grade" },
            { name: "year", label: "Year" },
          ]}
          data={educationData.phd}
          onInputChange={handleInputChange}
          onFileChange={(file) => handleFileChange("phd", file)}
          certificateFile={certificates.phd}
        />
        <EducationFormSection
          title="Other"
          level="other"
          fields={[
            { name: "institution", label: "Institution/Issuer" },
            { name: "course", label: "Course/Certificate Name" },
            { name: "grade", label: "Grade/Classification" },
            { name: "years", label: "Year" },
          ]}
          data={educationData.other}
          onInputChange={handleInputChange}
          onFileChange={(file) => handleFileChange("other", file)}
          certificateFile={certificates.other}
        />
      </form>

      <div className="pt-4 flex gap-3">
        <button type="button" onClick={onBack} className="button">
          <span className="button_lg">
            <span className="button_sl"></span>
            Back
          </span>
        </button>

        <button
          type="button"
          onClick={handleNext}
          className={`button ${isNextEnabled ? "" : "opacity-50 cursor-not-allowed"
            }`}
          disabled={!isNextEnabled}
        >
          <span className="button_lg">
            <span className="button_sl"></span>
            Next
          </span>
        </button>
      </div>
    </div>
  );
};

export default EducationAndCertificatesStep;